import React, { useState, useEffect } from 'react';
import './Grafico.css';
import { Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import baseURL from '../../url';
import useSafeDataFetch from '../../../hooks/useSafeDataFetch';

ChartJS.register(
    CategoryScale,
    LinearScale,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

export default function Grafico1() {
    const [rifaSeleccionada, setRifaSeleccionada] = useState(null);
    const [chartData, setChartData] = useState({});
    const [totales, setTotales] = useState({
        reservados: 0,
        pendientes: 0,
        disponibles: 0,
        total: 0,
    });

    // Usar el hook seguro para cargar datos
    const { data: rifasData, loading: rifasLoading, error: rifasError } = useSafeDataFetch(`${baseURL}/rifaGet.php`);
    const { data: numerosData, loading: numerosLoading, error: numerosError } = useSafeDataFetch(`${baseURL}/numeroGet.php`);

    const rifas = rifasData?.rifas || [];
    const numeros = numerosData?.numeros || [];

    useEffect(() => {
        if (Array.isArray(rifas) && rifas.length > 0 && rifas[0] && rifas[0].idRifa) {
            // Seleccionar automáticamente la primera rifa si existe
            manejarCambioRifa(rifas[0].idRifa);
        } else {
            // Mostrar gráfico de ejemplo si no hay rifas
            mostrarGraficoEjemplo();
        }
    }, [rifas]);


    const manejarCambioRifa = (idRifa) => {
        if (!idRifa) return;
        setRifaSeleccionada(idRifa);
        const numerosFiltrados = Array.isArray(numeros) ? 
            numeros.filter(numero => numero && numero.idRifa && String(numero.idRifa) === String(idRifa)) : [];
        procesarDatos(numerosFiltrados);
    };

    const procesarDatos = (numerosFiltrados) => {
        let reservados = 0;
        let pendientes = 0;
        let disponibles = 0;

        if (Array.isArray(numerosFiltrados) && numerosFiltrados.length > 0) {
            numerosFiltrados.forEach(numero => {
                if (numero && numero.estado) {
                    if (numero.estado === 'reservado') {
                        reservados += 1;
                    } else if (numero.estado === 'pendiente') {
                        pendientes += 1;
                    } else if (numero.estado === 'disponible') {
                        disponibles += 1;
                    }
                }
            });
        }

        const total = reservados + pendientes + disponibles;

        // Actualizar los totales
        setTotales({ reservados, pendientes, disponibles, total });

        // Generar datos del gráfico
        generarGrafico(reservados, pendientes, disponibles);
    };

    const generarGrafico = (reservados, pendientes, disponibles) => {
        try {
            // Asegurar que los valores sean números válidos
            const reservadosValidos = typeof reservados === 'number' && !isNaN(reservados) ? reservados : 0;
            const pendientesValidos = typeof pendientes === 'number' && !isNaN(pendientes) ? pendientes : 0;
            const disponiblesValidos = typeof disponibles === 'number' && !isNaN(disponibles) ? disponibles : 0;

            const data = {
                labels: ['Reservados', 'Pendientes', 'Disponibles'],
                datasets: [
                    {
                        label: 'Estados de los Números',
                        data: [reservadosValidos, pendientesValidos, disponiblesValidos],
                        backgroundColor: ['#FF0000', '#ffa726', '#009429'],
                        hoverBackgroundColor: ['#FF0000', '#ff8c00', '#009429'],
                    },
                ],
            };
            setChartData(data);
        } catch (error) {
            console.error('Error generating chart:', error);
            mostrarGraficoEjemplo();
        }
    };

    const mostrarGraficoEjemplo = () => {
        const dataEjemplo = {
            labels: ['Reservados', 'Pendientes', 'Disponibles'],
            datasets: [
                {
                    label: 'Gráfico de Ejemplo',
                    data: [10, 5, 15],
                    backgroundColor: ['#FF0000', '#ffa726', '#009429'],
                    hoverBackgroundColor: ['#FF0000', '#ff8c00', '#009429'],
                },
            ],
        };
        setChartData(dataEjemplo);
        setTotales({
            reservados: 10,
            pendientes: 5,
            disponibles: 15,
            total: 30,
        });
    };

    return (
        <div className="GraficoContent">
            <h3 className="titleGrafico">Estados de los Números por Rifa</h3>

            {/* Selección de rifa */}
            <div className="selector-rifas">
                <select
                    id="rifaSelect"
                    value={rifaSeleccionada || ''}
                    onChange={(e) => manejarCambioRifa(e.target.value)}
                >
                    <option value="" disabled>Selecciona una rifa</option>
                    {Array.isArray(rifas) && rifas.length > 0 ? rifas.filter(rifa => rifa && rifa.idRifa).map(rifa => (
                        <option key={rifa.idRifa} value={rifa.idRifa}>
                            {rifa.nombre || 'Sin nombre'}
                        </option>
                    )) : (
                        <option value="" disabled>No hay rifas disponibles</option>
                    )}
                </select>
            </div>

            {/* Totales */}
            <div className="totales">
                <span><strong>Reservados:</strong> {totales.reservados}</span>
                <span><strong>Pendientes:</strong> {totales.pendientes}</span>
                <span><strong>Disponibles:</strong> {totales.disponibles}</span>
                <span><strong>Total:</strong> {totales.total}</span>
            </div>

            {/* Gráfico */}
            <div className="grafico-container">
                {rifasLoading || numerosLoading ? (
                    <div style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        color: '#666',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px'
                    }}>
                        Cargando datos...
                    </div>
                ) : rifasError || numerosError ? (
                    <div style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        color: '#dc3545',
                        backgroundColor: '#f8d7da',
                        borderRadius: '8px'
                    }}>
                        Error al cargar los datos
                    </div>
                ) : chartData && chartData.datasets && chartData.datasets.length > 0 ? (
                    <Pie data={chartData} />
                ) : (
                    <div style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        color: '#666',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px'
                    }}>
                        No hay datos disponibles
                    </div>
                )}
            </div>
        </div>
    );
}
